// ============================================================================
// Facebook Messenger Adapter — public surface + registration helper.
//
// This file is the one the rest of the module imports. At module-load time,
// `registerFacebookMessengerAdapter()` wires the implementation into BOTH
// registries:
//
//   1. `webhook-adapters` (slug-keyed)  — powers the public ingress route
//      `POST /api/v1/connectors/webhooks/facebook-messenger/:instanceId`.
//   2. `capability-registry` (typeId-keyed)  — lets any cross-module caller
//      reach Messenger via `call(instanceId, 'messaging', 'sendMessage', ...)`.
//
// Matching up the keys: the `002_facebook_messenger_seed.sql` migration sets
// `connectors_types.id = connectors_types.slug = 'facebook-messenger'`, so
// the same string addresses the row from every angle (instance.type_id, URL
// path, capability register, webhook register).
// ============================================================================

import { register as registerCapability } from '../../services/capability-registry'
import { registerWebhookAdapter } from '../../services/webhook-adapters'
import { buildFacebookMessagingMethods } from './capability-methods'
import { facebookMessengerWebhookAdapter } from './webhook'

/** Canonical type id / slug. MUST match the `connectors_types` seed row. */
export const FB_MESSENGER_TYPE_SLUG = 'facebook-messenger'

export interface RegisterFacebookMessengerOpts {
  /** Injectable fetch — tests swap it out. Defaults to the global `fetch`. */
  fetchImpl?: typeof fetch
}

/**
 * Register the Facebook Messenger adapter in both the webhook and capability
 * registries. Idempotent: re-calling just overwrites the prior registration
 * (matches the semantics of the underlying registries). Safe to call at
 * module-load + test-reset points without special-casing.
 */
export function registerFacebookMessengerAdapter(
  opts: RegisterFacebookMessengerOpts = {},
): void {
  registerWebhookAdapter(FB_MESSENGER_TYPE_SLUG, facebookMessengerWebhookAdapter)
  registerCapability(
    FB_MESSENGER_TYPE_SLUG,
    'messaging',
    buildFacebookMessagingMethods(opts),
  )
}

// ============================================================================
// Barrel — re-export the pieces consumers might reach for directly.
// ============================================================================

export { facebookMessengerWebhookAdapter } from './webhook'
export { buildFacebookMessagingMethods } from './capability-methods'
export type { FacebookMessagingMethodsDeps } from './capability-methods'

export {
  FB_OAUTH_AUTHORIZE_URL,
  FB_GRAPH_API_BASE,
  FB_MESSENGER_DEFAULT_SCOPES,
  FB_MESSENGER_SUBSCRIBED_FIELDS,
  buildAuthorizeUrl,
  extendToken,
  getLongLivedPageToken,
  listUserPages,
  subscribeApp,
  subscribeAppWebhook,
  FacebookMessengerOAuthError,
  type FacebookMessengerAuthorizeInput,
  type FacebookMessengerAuthorizeResult,
  type ExtendTokenInput,
  type ExtendedUserToken,
  type GetLongLivedPageTokenInput,
  type LongLivedPageToken,
  type ListUserPagesInput,
  type UserPage,
  type SubscribeAppInput,
  type SubscribeAppWebhookInput,
  type FacebookMessengerOAuthErrorCode,
} from './oauth'

export {
  sendMessage,
  sendTyping,
  markAsRead,
  type SendMessageInput,
  type SendMessageOutput,
  type SendTypingInput,
  type MarkAsReadInput,
} from './send'

export { fetchUserProfile } from './profile'

export {
  FacebookMessengerError,
  isTokenRevoked,
  isPageUnlinked,
  parseRetryAfterMs,
  classifyFacebookError,
  type FacebookSendResponse,
  type FacebookUserProfile,
  type FacebookGraphError,
  type FacebookErrorEnvelope,
  type FacebookMessengerErrorCode,
  type FacebookMessengerErrorDetails,
} from './types'
